import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';
import { apiBaseUrl } from '../../../util/api';

import { SocialLoginButton } from '../../../components';

import { FacebookLogo, GoogleLogo, LinkedInLogo, MicrosoftLogo } from '../socialLoginLogos';

import css from './SignupForm.module.css';
import authCss from '../AuthenticationPage.module.css';

// The kind an invitation to a company's owner or control person carries.
const OWNER_INVITE_KIND = 'company_owner';

const SSO_AUTH_FLOW_KEY = 'ssoAuthFlow';
const SSO_SELECTED_USER_TYPE_KEY = 'ssoSelectedUserType';

const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const { isLogin, showFacebookLogin, showGoogleLogin, showLinkedInLogin, showMicrosoftLogin, from, userType = 'candidate' } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin || showLinkedInLogin || showMicrosoftLogin;

  const persistSsoContext = () => {
    if (typeof window === 'undefined') return;

    const selectedSignupUserType = userType || window.localStorage.getItem('selectUserType');
    window.localStorage.setItem(SSO_AUTH_FLOW_KEY, 'signup');

    if (selectedSignupUserType && selectedSignupUserType !== 'undefined') {
      window.localStorage.setItem(SSO_SELECTED_USER_TYPE_KEY, selectedSignupUserType);
    } else {
      window.localStorage.removeItem(SSO_SELECTED_USER_TYPE_KEY);
    }
  };

  const getDataForSSORoutes = () => {
    const baseUrl = apiBaseUrl();

    // For invite signup links, redirect existing users back to signuplink page for email verification
    const defaultReturn = typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : pathByRouteName('LandingPage', routeConfiguration);

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

    const queryParams = new URLSearchParams({
      ...(defaultReturn ? { defaultReturn } : {}),
      ...(defaultConfirm ? { defaultConfirm } : {}),
      authFlow: 'signup',
      ...(from ? { from } : {}),
      ...(userType ? { userType } : {}),
    });

    return { baseUrl, queryParams: queryParams.toString() };
  };

  const authWithFacebook = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/facebook?${queryParams}`;
  };

  const authWithGoogle = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/google?${queryParams}`;
  };

  const authWithLinkedIn = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/linkedin?${queryParams}`;
  };

  const authWithMicrosoft = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    persistSsoContext();
    window.location.href = `${baseUrl}/api/auth/microsoft?${queryParams}`;
  };

  return showSocialLogins ? (
    <div className={authCss.idpButtons}>
      {showFacebookLogin ? (
        <div className={authCss.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={authCss.buttonIcon}>{FacebookLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={authCss.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={authCss.buttonIcon}>{GoogleLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showLinkedInLogin ? (
        <div className={authCss.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithLinkedIn()}>
            <span className={authCss.buttonIcon}>{LinkedInLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithLinkedIn" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithLinkedIn" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showMicrosoftLogin ? (
        <div className={authCss.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithMicrosoft()}>
            <span className={authCss.buttonIcon}>{MicrosoftLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithMicrosoft" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithMicrosoft" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

const SignupFormLink = props => {
  const {
    rootClassName,
    className,
    inviteData,
    selectUserType,
  } = props;

  const userType = selectUserType || inviteData?.userType || 'candidate';
  const isOwnerInvite = inviteData?.inviteKind === OWNER_INVITE_KIND;
  const classes = classNames(rootClassName || css.root, className);

  const [emailMismatchError, setEmailMismatchError] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mismatchError = window.localStorage.getItem('emailMismatchError');
      if (mismatchError) {
        setEmailMismatchError(true);
        window.localStorage.removeItem('emailMismatchError');
      }
    }
  }, []);

  if (typeof window !== 'undefined' && inviteData?.email) {
    window.localStorage.setItem('invitedMail', inviteData.email);
  }

  return (
    <div className={classes}>
      {emailMismatchError && (
        <div className={css.emailMismatchError}>
          <p>
            The email associated with your sign-in does not match the invited email address
            {inviteData?.email ? <> (<b>{inviteData.email}</b>)</> : null}.
            Please sign up using the correct account.
          </p>
        </div>
      )}
      {isOwnerInvite ? (
        // A person a company names as its owner is not joining that company's team.
        <div className={css.inviteNote}>
          <h4>
            <FormattedMessage
              id="SignupFormLink.ownerInvite.title"
              values={{ companyName: inviteData.orgName }}
            />{' '}
            <FormattedMessage id="SignupFormLink.ownerInvite.body" />
          </h4>
          {inviteData.email && (
            <p>
              <FormattedMessage
                id="SignupFormLink.ownerInvite.email"
                values={{ email: <b>{inviteData.email}</b> }}
              />
            </p>
          )}
        </div>
      ) : (
        <div className={css.inviteNote}>
          <h4>
            Hi! You've been invited to join <b>{inviteData?.orgName}</b>.
            Please sign up using one of the options below to get started.
          </h4>
          {inviteData?.email && (
            <p>Please sign up with <b>{inviteData.email}</b> to accept your invitation.</p>
          )}
        </div>
      )}
      <div>
      <SocialLoginButtonsMaybe
        isLogin={false}
        showFacebookLogin={false}
        showGoogleLogin={true}
        showLinkedInLogin={true}
        showMicrosoftLogin={true}
        userType={userType}
      />
      </div>
    </div>
  );
};

export default SignupFormLink;
